import { memo, useEffect, useRef } from 'react';
import { useTheme } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimeField } from '@mui/x-date-pickers/TimeField';
import { Dayjs } from 'dayjs';
import { useSubscription } from 'services/hooks';
import {
  PERSONAL_ACCOUNT_MAX_DURATION,
  PRO_ACCOUNT_MAX_DURATION,
  MIN_DURATION,
  currentDate,
  personalAccountMaxDate,
  proAccountMaxDate,
  minDate,
  maxMinutes,
  maxSeconds,
  minMinutes,
  minSeconds,
} from './constants';
import { DurationSlider, DurationSliderContainer } from './styled';
import { DurationSelectorProps } from './types';

/**
 * DurationSelector lets users select a duration for their song.
 * It handles both keyboard and mouse inputs. Some keyboard inputs are manually controlled by key listeners,
 * since MUI doesn't expose all required input APIs otherwise.
 */
const DurationSelector = ({
  value,
  onChange,
  disabled,
}: DurationSelectorProps) => {
  const theme = useTheme();
  const { isPersonalAccount } = useSubscription();

  const maxDate = isPersonalAccount
    ? personalAccountMaxDate
    : proAccountMaxDate;

  const maxDuration = isPersonalAccount
    ? PERSONAL_ACCOUNT_MAX_DURATION
    : PRO_ACCOUNT_MAX_DURATION;

  const current = currentDate.add(value, 'seconds');

  const inputRef = useRef<HTMLInputElement>();

  const dataRef = useRef<Dayjs | null>(null);

  useEffect(() => {
    dataRef.current = current;
  }, [current]);

  useEffect(() => {
    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Backspace') {
        e.preventDefault();
        e.stopPropagation();
      }

      if (
        dataRef.current &&
        dataRef.current.minute() === maxMinutes &&
        dataRef.current.second() === maxSeconds &&
        e.key === 'ArrowUp'
      ) {
        onChange(maxDuration);
        e.preventDefault();
        e.stopPropagation();
      }

      if (
        dataRef.current &&
        dataRef.current.minute() === minMinutes &&
        dataRef.current.second() === minSeconds &&
        e.key === 'ArrowDown'
      ) {
        onChange(MIN_DURATION);
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const inputElement = inputRef.current;
    inputElement?.addEventListener('keydown', handleKeydown);

    return () => {
      inputElement?.removeEventListener('keydown', handleKeydown);
    };
  }, [onChange, maxDuration]);

  useEffect(() => {
    if (disabled) {
      onChange(MIN_DURATION);
    }
  }, [disabled, onChange]);

  useEffect(() => {
    if (value > maxDuration) {
      onChange(maxDuration);
      return;
    }
    if (value < MIN_DURATION) {
      onChange(MIN_DURATION);
    }
  }, [value, onChange, maxDuration]);

  const handleDurationChange = (newValue: number) => {
    onChange(newValue);
  };

  return (
    <DurationSliderContainer>
      <DurationSlider
        min={MIN_DURATION}
        max={maxDuration}
        value={value}
        disabled={disabled}
        onChange={({ target }) => {
          // @ts-expect-error incorrect MUI type, value actually exists
          handleDurationChange(target.value);
        }}
      />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <TimeField
          InputProps={{
            inputRef,
          }}
          disabled={disabled}
          value={current}
          onChange={(newValue) => {
            if (!newValue) {
              return;
            }
            const duration = newValue.diff(currentDate, 'seconds');
            handleDurationChange(duration);
          }}
          minTime={minDate}
          maxTime={maxDate}
          format="mm:ss"
          size="small"
          sx={{
            '& .MuiInputBase-root': {
              maxWidth: theme.spacing(13),
              borderRadius: theme.spacing(0.5),
            },
          }}
        />
      </LocalizationProvider>
    </DurationSliderContainer>
  );
};

export default memo(DurationSelector);
